
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Book } from '@/types';
import { useToast } from './use-toast';

export const useBooks = (authorId?: string) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['books', authorId],
    queryFn: async (): Promise<Book[]> => {
      let query = supabase
        .from('books')
        .select(`
          *,
          profiles:author_id (
            name,
            email
          )
        `)
        .order('publish_date', { ascending: false });
      
      if (authorId) {
        query = query.eq('author_id', authorId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        toast({
          title: 'Error loading books',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return data.map(book => ({
        ...book,
        publishDate: book.publish_date,
        authorId: book.author_id,
        format: book.format || 'paperback',
        author: book.profiles ? {
          id: book.author_id,
          name: book.profiles.name,
          email: book.profiles.email
        } : undefined
      }));
    }
  });
};

export const useBook = (bookId: string) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['book', bookId],
    queryFn: async (): Promise<Book> => {
      const { data, error } = await supabase
        .from('books')
        .select(`
          *,
          profiles:author_id (
            name,
            email
          )
        `)
        .eq('id', bookId)
        .single();
      
      if (error) {
        toast({
          title: 'Error loading book',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return {
        ...data,
        publishDate: data.publish_date,
        authorId: data.author_id,
        author: data.profiles ? {
          id: data.author_id,
          name: data.profiles.name,
          email: data.profiles.email
        } : undefined
      };
    },
    enabled: !!bookId
  });
};

export const useDeleteBook = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (bookId: string) => {
      const { error } = await supabase
        .from('books')
        .delete()
        .eq('id', bookId);

      if (error) {
        toast({
          title: 'Error deleting book',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['books'] });
      toast({
        title: 'Success',
        description: 'Book has been deleted successfully.',
      });
    }
  });
};
